// hooks/useLazyWebSocket.js
import { useState, useRef, useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { auth } from "../../api/firebase";

export default function useLazyWebSocket(wsUrl, options = {}) {
  const { inactivityTimeout = 70000, ...libraryOptions } = options;
  const [shouldConnect, setShouldConnect] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [wsUrlWithToken, setWsUrlWithToken] = useState(wsUrl);
  const timeoutRef = useRef(null);

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    shouldConnect ? wsUrlWithToken : null,
    {
      ...libraryOptions,
      onOpen: () => {
        console.log("🔌 WebSocket opened successfully");
        console.log("🔌 WebSocket readyState:", readyState);
        setConnectionError(null); // Clear any previous errors
        if (libraryOptions.onOpen) libraryOptions.onOpen();

        // Auto-close after inactivity
        timeoutRef.current = setTimeout(() => {
          console.log("⏲️ Closing due to inactivity");
          setShouldConnect(false);
        }, inactivityTimeout);
      },
      onClose: (event) => {
        console.log("❌ WebSocket closed", event.code, event.reason);
        if (libraryOptions.onClose) libraryOptions.onClose();
        clearTimeout(timeoutRef.current);
      },
      onMessage: (event) => {
        if (libraryOptions.onMessage) libraryOptions.onMessage(event);
      },
      onError: (event) => {
        console.error("🚨 WebSocket error", event);
        setConnectionError("Failed to establish WebSocket connection");
      },
      shouldReconnect: (closeEvent) => false,
      share: false,
    },
  );

  const connect = useCallback(async () => {
    console.log("📞 Connecting WebSocket...");
    setConnectionError(null); // Clear any previous errors

    // Force disconnect first to ensure clean state
    setShouldConnect(false);

    try {
      const user = auth.currentUser;
      console.log("user", user);
      if (!user) throw new Error("User not signed in");

      const idToken = await user.getIdToken(/* forceRefresh */ true);
      //console.log("idToken", idToken); // Commented out for cleaner logs

      // Add token as query parameter instead of subprotocol
      const urlWithToken = `${wsUrl}?token=${encodeURIComponent(idToken)}`;
      setWsUrlWithToken(urlWithToken);
      console.log("Setting URL with token as query parameter");

      // Connect immediately after setting URL
      console.log("🔄 Setting shouldConnect to true");
      setShouldConnect(true);
    } catch (err) {
      console.error("❌ Failed to get Firebase token", err);
      setConnectionError(err?.message || "Firebase token error");
    }
  }, []);

  const disconnect = useCallback(() => {
    console.log("🛑 Disconnecting WebSocket...");
    clearTimeout(timeoutRef.current);
    setShouldConnect(false);
    setConnectionError(null); // Clear error when disconnecting
  }, []);

  const isConnected = readyState === ReadyState.OPEN;

  return {
    connect,
    disconnect,
    sendMessage,
    lastMessage,
    readyState,
    isConnected,
    getWebSocket,
    connectionError,
  };
}
